/**
 * The side panels (spec 08 §2): 1UP on the left, 2UP on the right, drawn in the game's font
 * onto a 640x360 canvas laid exactly over the upscaled image, so the HUD is pixel art at the
 * same scale as the frame. What it shows comes in as plain data; it knows nothing of the sim.
 */

import { CELL, drawText } from '../font8';

export interface PanelState {
  /** False shows PUSH FIRE TO JOIN instead of a score. */
  playing: boolean;
  score: number;
  lives: number;
  /** Which of E, X, T, E, N, D have been collected. */
  extend: readonly boolean[];
}

export interface HudState {
  /** The round shown: its number, or text (ALL after the ending, ? in the secret room). */
  level: number | string;
  hiScore: number;
  players: readonly [PanelState, PanelState];
  /** The C64 cleared its whole screen for the story: no panels then. */
  hidden?: boolean;
}

export const HUD_W = 640;
export const HUD_H = 360;
/** Width of each side panel: what the 448-texel playfield leaves of 640. */
export const PANEL_W = 96;

/** Colours by role; the HUD's own small palette, taken from the core ramps. */
export interface HudColours {
  p1: string;
  p2: string;
  text: string;
  dim: string;
  accent: string;
}

const LIFE_ICON = ['..##..', '.####.', '######', '#.##.#', '######', '.#..#.'];
const EXTEND = 'EXTEND';

const pad = (n: number, width: number): string => String(Math.max(0, Math.floor(n))).padStart(width, '0');

function icon(ctx: CanvasRenderingContext2D, rows: readonly string[], x: number, y: number, colour: string): void {
  ctx.fillStyle = colour;
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) if (row[c] === '#') ctx.fillRect(x + c, y + r, 1, 1);
  });
}

/** Text centred in the panel whose left edge is `x0`. */
function centred(ctx: CanvasRenderingContext2D, text: string, x0: number, y: number, colour: string): void {
  drawText(ctx, text, x0 + Math.floor((PANEL_W - text.length * CELL) / 2), y, colour);
}

function panel(ctx: CanvasRenderingContext2D, s: PanelState, x0: number, label: string, own: string, c: HudColours): void {
  centred(ctx, label, x0, 24, own);
  if (!s.playing) {
    ['PUSH', 'FIRE', 'TO', 'JOIN'].forEach((w, i) => centred(ctx, w, x0, 48 + i * 12, c.dim));
    return;
  }
  centred(ctx, pad(s.score, 8).slice(-8), x0, 36, c.text);
  for (let i = 0; i < Math.min(s.lives, 8); i++) icon(ctx, LIFE_ICON, x0 + 16 + (i % 4) * 16, 56 + Math.floor(i / 4) * 10, own);
  [...EXTEND].forEach((ch, i) => {
    drawText(ctx, ch, x0 + 24 + (i % 3) * 16, 88 + Math.floor(i / 3) * 12, s.extend[i] ? c.accent : c.dim);
  });
}

/** Draw the whole HUD for one frame; the canvas is cleared first. */
export function drawHud(ctx: CanvasRenderingContext2D, s: HudState, c: HudColours): void {
  ctx.clearRect(0, 0, HUD_W, HUD_H);
  if (s.hidden) return;
  panel(ctx, s.players[0], 0, '1UP', c.p1, c);
  panel(ctx, s.players[1], HUD_W - PANEL_W, '2UP', c.p2, c);
  centred(ctx, 'HIGH', 0, 132, c.dim);
  centred(ctx, pad(s.hiScore, 8), 0, 144, c.text);
  centred(ctx, 'ROUND', HUD_W - PANEL_W, 132, c.dim);
  centred(ctx, String(s.level), HUD_W - PANEL_W, 144, c.text);
}

/**
 * A HUD canvas over the game image: 640x360 backing pixels, CSS-scaled to the image's rect
 * with nearest-neighbour sampling. Call `place` with the pipeline's `viewRect` each frame.
 */
export function createHudCanvas(parent: HTMLElement): { canvas: HTMLCanvasElement; place: (r: DOMRectLike) => void } {
  const canvas = document.createElement('canvas');
  canvas.width = HUD_W;
  canvas.height = HUD_H;
  Object.assign(canvas.style, { position: 'fixed', imageRendering: 'pixelated', pointerEvents: 'none' });
  parent.appendChild(canvas);
  return {
    canvas,
    place(r: DOMRectLike): void {
      Object.assign(canvas.style, { left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });
    },
  };
}

export interface DOMRectLike {
  left: number;
  top: number;
  width: number;
  height: number;
}
